// src/components/commercial/AmbassadorVisitReview.jsx
//
// Admin page: ambassador check-ins whose GPS didn't match the prospect -
// WARNING (100-250 m) and REVIEW (beyond, or no position; the ambassador
// had to explain in a comment). Accept or reject each one; rejected visits
// won't earn XP or commissions (lots 2-3). Backed by
// /admin/commercial/ambassador/visits (backend/routes/commercial/ambassadorAdmin.ts).
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Container, Typography, Box, Button, Paper, Stack, Alert, Chip, CircularProgress, Link, FormControlLabel, Switch,
} from '@mui/material';
import useCommercialAdmin from '../../utils/useCommercialAdmin';
import { commercialGet, commercialPut } from '../../utils/commercialApi';

const RESULTS = {
  OWNER_ABSENT: 'Gérant absent', CALL_BACK: 'À rappeler', INTERESTED: 'Intéressé', REFUSED: 'Refus',
  INSTALLATION_ACCEPTED: 'Installation acceptée', CLOSED: 'Fermé', BAD_RECORD: 'Fiche erronée', OTHER: 'Autre',
};
const osm = (lat, lon) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=18/${lat}/${lon}`;

export default function AmbassadorVisitReview() {
  const { ready, forbidden, loading: authLoading, getToken } = useCommercialAdmin();
  const [visits, setVisits] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!ready) return;
    commercialGet('/ambassador/visits', getToken(), { pending: showAll ? '0' : '1' })
      .then((d) => setVisits(d.visits))
      .catch((e) => setError(e.message));
  }, [ready, getToken, showAll]);
  useEffect(() => { load(); }, [load]);

  const review = async (id, outcome) => {
    try {
      await commercialPut(`/ambassador/visits/${id}/review`, getToken(), { outcome });
      load();
    } catch (e) { setError(e.message); }
  };

  if (authLoading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;
  if (forbidden) return <Container sx={{ py: 4 }}><Alert severity="error">Accès réservé aux administrateurs.</Alert></Container>;

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4">Visites à vérifier</Typography>
        <Stack direction="row" spacing={1}>
          <Button component={RouterLink} to="/admin/commercial/ambassador/prospects">Prospects</Button>
          <Button component={RouterLink} to="/admin/commercial">← Dashboard</Button>
        </Stack>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visites dont la position du téléphone ne correspondait pas à l’établissement : entre 100 et 250 m
        (à surveiller), au-delà ou sans position (à vérifier, avec le commentaire de l’ambassadeur).
        Le GPS en ville peut se tromper de plusieurs dizaines de mètres.
      </Typography>
      <FormControlLabel sx={{ mb: 1 }} control={<Switch checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />}
        label="Afficher aussi les visites déjà traitées" />
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {!visits && <CircularProgress size={24} />}
      {visits?.length === 0 && <Alert severity="success">Rien à vérifier.</Alert>}
      <Stack spacing={1.5}>
        {visits?.map((v) => (
          <Paper key={v.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
              <Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip size="small" color={v.gps_check === 'REVIEW' ? 'error' : 'warning'}
                    label={v.gps_check === 'REVIEW' ? 'À vérifier' : 'À surveiller'} />
                  <Typography variant="subtitle1">{v.business_name}</Typography>
                </Stack>
                <Typography variant="body2">
                  {v.agent} · {new Date(v.checkin_at).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })} ·{' '}
                  {v.distance_m != null ? `${v.distance_m} m de l’établissement` : 'position refusée par le téléphone'}
                  {v.gps_accuracy != null ? ` (précision ± ${v.gps_accuracy} m)` : ''}
                </Typography>
                <Typography variant="body2">Résultat : {RESULTS[v.result] ?? 'pas encore donné'}{v.comment ? ` - « ${v.comment} »` : ''}</Typography>
                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                  {v.latitude && <Link href={osm(v.latitude, v.longitude)} target="_blank" rel="noreferrer" variant="body2">Établissement</Link>}
                  {v.checkin_lat && <Link href={osm(v.checkin_lat, v.checkin_lon)} target="_blank" rel="noreferrer" variant="body2">Position du check-in</Link>}
                </Stack>
              </Box>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                {v.review_outcome ? (
                  <Chip label={v.review_outcome === 'ACCEPTED' ? `Acceptée (${v.reviewed_by})` : `Rejetée (${v.reviewed_by})`} />
                ) : (
                  <>
                    <Button size="small" variant="contained" color="success" onClick={() => review(v.id, 'ACCEPTED')}>Accepter</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => review(v.id, 'REJECTED')}>Rejeter</Button>
                  </>
                )}
              </Stack>
            </Stack>
          </Paper>
        ))}
      </Stack>
    </Container>
  );
}
